import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiX, FiUpload } from 'react-icons/fi';
import axios from 'axios';

const SpecialistsTab = ({ vendorId, vendorData, onDataChange }) => {
  const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api`;
  const token = localStorage.getItem('token');
  const authHeader = { Authorization: `Bearer ${token}` };

  const [specialists, setSpecialists] = useState(vendorData?.specialists || []);
  const [vendorServices, setVendorServices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingSpecialist, setEditingSpecialist] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [form, setForm] = useState({
    name: '',
    status: true,
    services: [],
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    setSpecialists(vendorData?.specialists || []);
  }, [vendorData]);

  useEffect(() => {
    if (vendorId) fetchVendorServices();
  }, [vendorId]);

  const fetchVendorServices = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/vendor-service/${vendorId}`, {
        headers: authHeader,
      });
      if (res.data.success) {
        setVendorServices(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching vendor services:', err);
    }
  };

  const openAddModal = () => {
    setEditingSpecialist(null);
    setForm({ name: '', status: true, services: [] });
    setImageFile(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEditModal = (specialist) => {
    setEditingSpecialist(specialist);
    setForm({
      name: specialist.name || '',
      status: specialist.status !== false,
      services: (specialist.services || []).map(s => typeof s === 'object' ? s._id : s),
    });
    setImageFile(null);
    setImagePreview(specialist.image || null);
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const toggleService = (serviceId) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert('Name is required');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('status', form.status.toString());
      formData.append('services', JSON.stringify(form.services));

      if (!editingSpecialist) {
        formData.append('vendorId', vendorId);
      }

      if (imageFile) {
        formData.append('image', imageFile);
      }

      const url = editingSpecialist
        ? `${API_BASE_URL}/admin/vendor-specialist/${editingSpecialist._id}`
        : `${API_BASE_URL}/admin/vendor-specialist`;

      const method = editingSpecialist ? 'put' : 'post';

      await axios[method](url, formData, {
        headers: {
          ...authHeader,
          'Content-Type': 'multipart/form-data',
        },
      });

      setShowModal(false);
      onDataChange();
    } catch (err) {
      console.error('Error saving specialist:', err);
      alert(err.response?.data?.message || 'Failed to save specialist');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (specialist) => {
    if (!window.confirm(`Are you sure you want to delete ${specialist.name}?`)) return;

    setDeleting(specialist._id);
    try {
      await axios.delete(`${API_BASE_URL}/admin/vendor-specialist/${specialist._id}`, {
        headers: authHeader,
      });
      onDataChange();
    } catch (err) {
      console.error('Error deleting specialist:', err);
      alert(err.response?.data?.message || 'Failed to delete specialist');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-gray-800">Specialists</h3>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
        >
          <FiPlus /> Add Specialist
        </button>
      </div>

      {specialists.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {specialists.map((specialist) => (
            <div key={specialist._id} className="bg-white rounded-3xl shadow-card overflow-hidden border border-gray-200">
              <div className="relative h-48">
                {specialist.image ? (
                  <img
                    src={specialist.image}
                    alt={specialist.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400 text-sm">
                    No Image
                  </div>
                )}
              </div>
              <div className="p-4">
                <h4 className="font-bold text-lg mb-1">{specialist.name}</h4>
                <div className="flex items-center mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    specialist.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {specialist.status ? 'Active' : 'Inactive'}
                  </span>
                  {specialist.services && specialist.services.length > 0 && (
                    <span className="ml-2 text-xs text-gray-500">
                      {specialist.services.length} service{specialist.services.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {specialist.services && specialist.services.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {specialist.services.slice(0, 3).map((service, idx) => (
                      <span key={idx} className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
                        {typeof service === 'object' ? service.name : service}
                      </span>
                    ))}
                    {specialist.services.length > 3 && (
                      <span className="text-xs text-gray-400">+{specialist.services.length - 3} more</span>
                    )}
                  </div>
                )}
                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => openEditModal(specialist)}
                    className="flex-1 flex items-center justify-center gap-1 text-sm text-blue-600 hover:bg-blue-50 py-1.5 rounded transition-colors"
                  >
                    <FiEdit size={14} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(specialist)}
                    disabled={deleting === specialist._id}
                    className="flex-1 flex items-center justify-center gap-1 text-sm text-red-600 hover:bg-red-50 py-1.5 rounded transition-colors disabled:opacity-50"
                  >
                    <FiTrash2 size={14} /> {deleting === specialist._id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 italic">No specialists added yet</p>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-bold">
                {editingSpecialist ? 'Edit Specialist' : 'Add Specialist'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <FiX size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter specialist name"
                />
              </div>

              {/* Status */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Status</label>
                <button
                  onClick={() => setForm({ ...form, status: !form.status })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.status ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    form.status ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                {imagePreview && (
                  <div className="mb-2">
                    <img src={imagePreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />
                  </div>
                )}
                <label className="flex items-center gap-2 cursor-pointer text-sm text-purple-600 hover:text-purple-700">
                  <FiUpload />
                  {imageFile ? 'Change Image' : (editingSpecialist?.image ? 'Replace Image' : 'Upload Image')}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Services */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Services ({form.services.length} selected)
                </label>
                {vendorServices.length > 0 ? (
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {vendorServices.map((service) => (
                      <label
                        key={service._id}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={form.services.includes(service._id)}
                          onChange={() => toggleService(service._id)}
                          className="rounded text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm">{service.name}</span>
                        {service.price && (
                          <span className="text-xs text-gray-400 ml-auto">
                            ₹{service.price}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No services available for this vendor</p>
                )}
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : (editingSpecialist ? 'Update' : 'Add Specialist')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialistsTab;
